/**
 * The "Create" (plus button) menu, owner 2026-10-25 (option A of the mockups):
 * grouped rows Clients / Work / Time / Billing. Billing rows only for people
 * who may open Billing. No letter shortcuts (owner 2026-10-25: letters removed);
 * arrows + Enter + Esc only.
 */
export type CreateAction =
  | "individual"
  | "organization"
  | "work"
  | "startTimer"
  | "addTime"
  | "invoice"
  | "payment";

export type CreateGroup = "clients" | "work" | "time" | "billing";

export type CreateMenuItem = {
  action: CreateAction;
  group: CreateGroup;
  title: string;
  description: string;
};

export const CREATE_GROUP_LABELS: Record<CreateGroup, string> = {
  clients: "Clients",
  work: "Work",
  time: "Time",
  billing: "Billing",
};

const ALL_ITEMS: CreateMenuItem[] = [
  {
    action: "individual",
    group: "clients",
    title: "New individual",
    description: "A person you work for",
  },
  {
    action: "organization",
    group: "clients",
    title: "New organization",
    description: "A business, with its contact person",
  },
  {
    action: "work",
    group: "work",
    title: "New work item",
    description: "For one client or many at once",
  },
  {
    action: "startTimer",
    group: "time",
    title: "Start timer",
    description: "Track time on a work item now",
  },
  {
    action: "addTime",
    group: "time",
    title: "Add time",
    description: "Log time you already spent",
  },
  {
    action: "invoice",
    group: "billing",
    title: "New invoice",
    description: "Bill a client, add unbilled time",
  },
  {
    action: "payment",
    group: "billing",
    title: "Record payment",
    description: "Mark invoices as paid",
  },
];

export const createMenuItems = (canViewBilling: boolean): CreateMenuItem[] =>
  ALL_ITEMS.filter((item) => canViewBilling || item.group !== "billing");

/** The rows split into their groups, in menu order (empty groups left out). */
export const groupCreateItems = (
  items: CreateMenuItem[],
): { group: CreateGroup; items: CreateMenuItem[] }[] => {
  const groups: { group: CreateGroup; items: CreateMenuItem[] }[] = [];
  items.forEach((item) => {
    const last = groups[groups.length - 1];
    if (last && last.group === item.group) {
      last.items.push(item);
    } else {
      groups.push({ group: item.group, items: [item] });
    }
  });
  return groups;
};

/** Arrow keys move the highlight and wrap around at the ends. */
export const moveHighlight = (
  current: number,
  delta: number,
  count: number,
): number => {
  if (count <= 0) {
    return -1;
  }
  if (current < 0) {
    return delta > 0 ? 0 : count - 1;
  }
  return (current + delta + count) % count;
};
